import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Milan2026Admin from './Milan2026Admin';
import ModernFDProjetsList from './ModernFDProjetsList';
import * as api from '../../api';
import styled from 'styled-components';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  padding: 20px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
  margin: 20px 0;
`;

const ModernFDProjetsWrapper = () => {
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [milanSubmissions, setMilanSubmissions] = useState([]);
  const [milanPieces, setMilanPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMilan2026, setIsMilan2026] = useState(false);

  const eventId = searchParams.get('eid');

  useEffect(() => {
    // Always load Milan 2026 data for this route
    loadMilanDataAsProjects();
  }, [eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      const eventResponse = await api.getEventById(eventId);
      const eventData = eventResponse.data.event;
      setEvent(eventData);

      // Check if this is a Milan 2026 event
      const isMilanEvent = eventData.title_fr?.toLowerCase().includes('milan 2026') || 
                          eventData.title_fr?.toLowerCase().includes('milan2026');
      
      if (isMilanEvent) {
        setIsMilan2026(true);
        await loadMilanDataAsProjects();
      } else {
        setIsMilan2026(false);
        const usersResponse = await api.getParticipatingUsersWithData(eventId);
        setAllProjects(usersResponse.data.users);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading event data:', err);
      setError('Failed to load event data. Please check the event ID and try again.');
      setLoading(false);
    }
  };

  const loadMilanDataAsProjects = async () => {
    try {
      setLoading(true);
      const response = await api.getFormSubmissions({ formName: 'milan-2026' });
      const submissionsData = response.data.submissions || [];
      setMilanSubmissions(submissionsData);
      
      // Transform Milan 2026 submissions to look like FD100 projects
      const transformedProjects = [];
      submissionsData.forEach(submission => {
        if (submission.data.pieces && submission.data.pieces.length > 0) {
          submission.data.pieces.forEach((piece, index) => {
            const transformedProject = {
              projet: {
                _id: `${submission._id}_${index}`,
                name: piece.name,
                status: piece.status === 'pending' ? 'à valider' :
                       piece.status === 'approved' ? 'validé' :
                       piece.status === 'rejected' ? 'refusé' :
                       piece.status === 'waiting' ? 'en attente' : 'à valider',
                type: 'objet', // Default type for Milan pieces
                typologie: piece.typologie === 'autre' && piece.typologieOther ? piece.typologieOther : piece.typologie || '',
                projet_verifie_fd100_2025: piece.status === 'approved' ? 'oui' : 'non',
                candidate_as_person_fd100_25: 'non',
                fabrication_fr: 'non',
                description: piece.description || '',
                uploadedMedia: piece.uploadedMedia || [], // Include media data
                // Add partner information from piece level
                designerCompanyName: piece.designerCompanyName,
                fabricantCompanyName: piece.fabricantCompanyName,
                designerInternationalPresence: piece.designerInternationalPresence,
                fabricantInternationalPresence: piece.fabricantInternationalPresence,
                designerInternationalDetails: piece.designerInternationalDetails,
                fabricantInternationalDetails: piece.fabricantInternationalDetails,
                designerWebAndSocial: piece.designerWebAndSocial,
                fabricantWebAndSocial: piece.fabricantWebAndSocial,
                designerWebAndSocialDetails: piece.designerWebAndSocialDetails,
                fabricantWebAndSocialDetails: piece.fabricantWebAndSocialDetails,
                designerPresentation: piece.designerPresentation,
                fabricantPresentation: piece.fabricantPresentation,
                designerCodifab: piece.designerCodifab,
                fabricantCodifab: piece.fabricantCodifab,
                designerAmeublement: piece.designerAmeublement,
                fabricantAmeublement: piece.fabricantAmeublement,
                // Also include submission data for context
                submissionUserType: submission.data.userType,
                submissionDesignerCompanyName: submission.data.designerCompanyName,
                submissionFabricantCompanyName: submission.data.fabricantCompanyName
              },
              user: {
                user: {
                  _id: submission.userId?._id || submission._id,
                  email: submission.userId?.email || submission.data.email
                },
                participation: {
                  _id: submission._id,
                  createdAt: submission.createdAt
                },
                hsCompany: {
                  properties: {
                    name: submission.data.userType === 'designer'
                      ? submission.data.designerCompanyName
                      : submission.data.fabricantCompanyName,
                    cat_gorie_via: submission.data.userType === 'designer' ? 'Designer' : 'Fabricant'
                  }
                },
                company: submission.company, // This will now include the actual company data from the backend
                userId: submission.userId
              }
            };
            transformedProjects.push(transformedProject);
          });
        }
      });
      
      setAllProjects(transformedProjects);
      
      // Create a mock event for Milan 2026
      setEvent({
        _id: 'milan2026',
        title_fr: 'Milan 2026',
        type: 'design'
      });
      
      setIsMilan2026(true);
    } catch (err) {
      console.error('Error loading Milan 2026 data:', err);
      setError('Failed to load Milan 2026 submissions.');
    } finally {
      setLoading(false);
    }
  };

  const updateProjetStatus = async (participationId, projetId, status) => {
    try {
      // For Milan 2026, update the piece-level status
      // projetId is in format "submissionId_pieceIndex"
      const [submissionId, pieceIndexStr] = projetId.split('_');
      const pieceIndex = parseInt(pieceIndexStr);
      const newStatus = status === 'validé' ? 'approved' :
                       status === 'refusé' ? 'rejected' :
                       status === 'en attente' ? 'waiting' : 'pending';

      await api.updatePieceStatus(submissionId, pieceIndex, newStatus);

      // Update local state
      setAllProjects(prev => prev.map(project =>
        project.projet._id === projetId ? {
          ...project,
          projet: { ...project.projet, status }
        } : project
      ));
    } catch (err) {
      console.error('Error updating project status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const updateProjetCategorie = async (participationId, projetId, typologie) => {
    try {
      // For Milan 2026, update via API
      // projetId is in format "submissionId_pieceIndex"
      const [submissionId, pieceIndexStr] = projetId.split('_');
      const pieceIndex = parseInt(pieceIndexStr);

      await api.updatePieceCategory(submissionId, pieceIndex, typologie);

      // Update local state after successful API call
      setAllProjects(prev => prev.map(project =>
        project.projet._id === projetId ? {
          ...project,
          projet: { ...project.projet, typologie }
        } : project
      ));

      // Show success feedback
      console.log(`Category updated for project ${projetId}: ${typologie}`);
    } catch (err) {
      console.error('Error updating project category:', err);
      alert('Erreur lors de la mise à jour de la catégorie');
    }
  };

  const updateProjetVerificationStatus = async (participationId, companyId, projetId, status) => {
    try {
      // For Milan 2026, update local state only
      setAllProjects(prev => prev.map(project => 
        project.projet._id === projetId ? {
          ...project,
          projet: { ...project.projet, projet_verifie_fd100_2025: status }
        } : project
      ));
    } catch (err) {
      console.error('Error updating project verification:', err);
      alert('Erreur lors de la mise à jour de la vérification');
    }
  };

  const updateProjetCandidatingAsStatus = async (participationId, companyId, projetId, status) => {
    try {
      // For Milan 2026, update local state only
      setAllProjects(prev => prev.map(project => 
        project.projet._id === projetId ? {
          ...project,
          projet: { ...project.projet, candidate_as_person_fd100_25: status }
        } : project
      ));
    } catch (err) {
      console.error('Error updating candidating as person status:', err);
      alert('Erreur lors de la mise à jour du statut de candidature');
    }
  };

  if (error) {
    return (
      <ErrorContainer>
        <h3>Error</h3>
        <p>{error}</p>
      </ErrorContainer>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        Chargement des données Milan 2026...
      </div>
    );
  }

  if (!event || !allProjects.length) {
    return (
      <ErrorContainer>
        <h3>Aucune donnée disponible</h3>
        <p>Aucune soumission Milan 2026 trouvée.</p>
      </ErrorContainer>
    );
  }

  return (
    <ModernFDProjetsList
      projets={allProjects}
      event={event}
      updateProjetStatus={updateProjetStatus}
      updateProjetCategorie={updateProjetCategorie}
      updateProjetVerificationStatus={updateProjetVerificationStatus}
      updateProjetCandidatingAsStatus={updateProjetCandidatingAsStatus}
    />
  );
};

export default ModernFDProjetsWrapper;